export type IdeaTier = 'production' | 'nonprod';
export type IdeaStatus = 'requested' | 'planned' | 'in_progress' | 'shipped' | 'declined';

export interface IdeaSummary {
  id: number;
  title: string;
  institution_code: string;
  institution_name: string;
  status: IdeaStatus;
  withdrawn_at: string | null;
  display_status: IdeaStatus | 'withdrawn';
  estimate: string | null;
  total_points: number;
  backer_count: number;
  created_at: string;
  updated_at: string;
  status_changed_at: string;
}

export interface IdeaAttachment {
  id: number;
  file_name: string;
  content_type: string;
  byte_size: number;
  created_by_name: string;
  created_at: string;
}

export interface IdeaDetail extends IdeaSummary {
  description: string;
  decline_reason: string | null;
  created_by_name: string;
  created_by_principal_id: string;
  created_instance: string;
  backers: {
    institution_code: string;
    institution_name: string;
    points: number;
    created_by_name: string;
  }[];
  updates: {
    id: number;
    body: string;
    author_name: string;
    author_type: 'streamlyne';
    created_at: string;
  }[];
  ledger: {
    id: number;
    institution_code: string;
    institution_name: string;
    entry_type: string;
    points: number;
    idempotency_key: string;
    actor_name: string;
    actor_type: string;
    created_at: string;
  }[];
  attachments: IdeaAttachment[];
}

export type IdeaAction =
  | { action: 'plan'; estimate: string }
  | { action: 'start'; estimate?: string }
  | { action: 'ship' }
  | { action: 'decline'; reason: string }
  | { action: 'update'; body: string };

export const ideaStatusLabels: Record<IdeaSummary['display_status'], string> = {
  requested: 'Requested',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Released',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
};

export const ideaActionLabels: Record<IdeaAction['action'], string> = {
  plan: 'Plan',
  start: 'Start',
  ship: 'Release',
  decline: 'Decline',
  update: 'Post update',
};

export const allowedIdeaActions: Record<IdeaStatus, IdeaAction['action'][]> = {
  requested: ['plan', 'start', 'ship', 'decline', 'update'],
  planned: ['plan', 'start', 'ship', 'decline', 'update'],
  in_progress: ['start', 'ship', 'decline', 'update'],
  shipped: ['update'],
  declined: ['update'],
};

export function ideaErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = error.data;
    if (
      typeof data === 'object' &&
      data !== null &&
      'detail' in data &&
      typeof data.detail === 'string'
    ) {
      return data.detail;
    }
  }
  return fallback;
}
